import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Input, useDebounceCallback } from '@devhop/ui';
import { SearchIcon } from 'lucide-react';
import { SimpleSelect } from '@/shared/ui/SimpleSelect';

type Option = { value: string; label: string };

export type PromotionRecordsSearchParams = {
    offset?: number;
    limit?: number;
    sort?: Array<{ field: string; dir: 'asc' | 'desc' }>;
    employeeId?: string;
    periodYear?: number;
    periodMonth?: number;
    positionId?: string;
    search?: string;
};

type Props = {
    searchParams: PromotionRecordsSearchParams;
    fromRoute: '/app/hr/promotion/records' | '/app/hr/promotion/history';
    employeeOptions: Option[];
    positionOptions: Option[];
};

const VALUE_ALL = '__all__';

export function PromotionRecordsFilter({
    searchParams,
    fromRoute,
    employeeOptions,
    positionOptions,
}: Props) {
    const nav = useNavigate({ from: fromRoute });

    const [searchInput, setSearchInput] = useState(searchParams.search ?? '');
    const [employeeId, setEmployeeId] = useState(
        searchParams.employeeId ?? VALUE_ALL,
    );
    const [positionId, setPositionId] = useState(
        searchParams.positionId ?? VALUE_ALL,
    );
    const [periodYear, setPeriodYear] = useState(
        searchParams.periodYear ? String(searchParams.periodYear) : '',
    );
    const [periodMonth, setPeriodMonth] = useState(
        searchParams.periodMonth ? String(searchParams.periodMonth) : '',
    );

    useEffect(() => {
        setSearchInput(searchParams.search ?? '');
        setEmployeeId(searchParams.employeeId ?? VALUE_ALL);
        setPositionId(searchParams.positionId ?? VALUE_ALL);
        setPeriodYear(
            searchParams.periodYear ? String(searchParams.periodYear) : '',
        );
        setPeriodMonth(
            searchParams.periodMonth ? String(searchParams.periodMonth) : '',
        );
    }, [
        searchParams.search,
        searchParams.employeeId,
        searchParams.positionId,
        searchParams.periodYear,
        searchParams.periodMonth,
    ]);

    const doNav = (opts: Partial<{ empId: string; posId: string; yr: string; mon: string; searchVal: string }> = {}) => {
        const e = opts.empId ?? employeeId;
        const p = opts.posId ?? positionId;
        const y = opts.yr ?? periodYear;
        const m = opts.mon ?? periodMonth;
        const s = opts.searchVal ?? searchInput;
        nav({
            search: {
                offset: 0,
                limit: searchParams.limit ?? 20,
                sort: searchParams.sort,
                employeeId: e === VALUE_ALL ? undefined : e,
                positionId: p === VALUE_ALL ? undefined : p,
                periodYear: y ? Number(y) : undefined,
                periodMonth: m ? Number(m) : undefined,
                search: s.trim() || undefined,
            },
        });
    };

    const debouncedSearch = useDebounceCallback((val: string) => doNav({ searchVal: val }), 400);

    return (
        <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-[200px] flex-1">
                    <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={searchInput}
                        onChange={(e) => {
                            setSearchInput(e.target.value);
                            debouncedSearch(e.target.value);
                        }}
                        placeholder="ຄົ້ນຫາ..."
                        className="h-8 pl-8 sm:max-w-2xs"
                    />
                </div>
                <SimpleSelect
                    options={[
                        { value: VALUE_ALL, label: 'ທັງໝົດ' },
                        ...employeeOptions,
                    ]}
                    value={employeeId}
                    onValueChange={(v) => {
                        setEmployeeId(v);
                        doNav({ empId: v });
                    }}
                    placeholder="ພະນັກງານ"
                    className="h-8 w-full sm:w-40"
                />
                <SimpleSelect
                    options={[
                        { value: VALUE_ALL, label: 'ທັງໝົດ' },
                        ...positionOptions,
                    ]}
                    value={positionId}
                    onValueChange={(v) => {
                        setPositionId(v);
                        doNav({ posId: v });
                    }}
                    placeholder="ຕຳແໜ່ງ"
                    className="h-8 w-full sm:w-40"
                />
                <Input
                    value={periodYear}
                    onChange={(e) => {
                        setPeriodYear(e.target.value);
                        doNav({ yr: e.target.value });
                    }}
                    placeholder="ປີ"
                    className="h-8 w-full sm:w-24"
                />
                <Input
                    value={periodMonth}
                    onChange={(e) => {
                        setPeriodMonth(e.target.value);
                        doNav({ mon: e.target.value });
                    }}
                    placeholder="ເດືອນ"
                    className="h-8 w-full sm:w-24"
                />
            </div>
        </div>
    );
}
